import { useEffect } from "react";

const ORIGIN = process.env.REACT_APP_BACKEND_URL || "https://wi.heathersboutique.com";

// Google truncates the DISPLAYED title tag around ~55-60 chars in the SERP,
// but the full <title> counts for ranking + query-match. We use 68 as the
// hard cap so brand + color + spec + model can all coexist on product pages
// like "Periwinkle/Silver Amarra Sheath Prom Dress 89112" (47 chars) without
// getting truncated.
// NOTE: brand name is intentionally NOT appended to titles — pages own their
// own title copy end-to-end so we maximise the budget for keywords.
const TITLE_MAX = 68;
const DESC_MAX = 160;

function _clean(s) {
  return String(s || "").replace(/\s+/g, " ").trim();
}

function _clip(text, max, { ellipsis = true } = {}) {
  const t = _clean(text);
  if (t.length <= max) return t;

  // Prefer a natural sentence boundary within the budget so the meta reads
  // like a complete thought rather than a mid-clause cutoff. We take the
  // *last* .!? within the budget if it lands past ~55% of the length.
  const scan = t.slice(0, max);
  const punct = Math.max(scan.lastIndexOf(". "), scan.lastIndexOf("! "), scan.lastIndexOf("? "));
  if (punct >= max * 0.55) return scan.slice(0, punct + 1);

  // Otherwise trim to the last space within budget.
  const budget = ellipsis ? max - 1 : max;
  const cut = t.slice(0, budget);
  const lastSpace = cut.lastIndexOf(" ");
  let stem = (lastSpace > budget * 0.6 ? cut.slice(0, lastSpace) : cut)
    .replace(/[\s.,;:—-]+$/g, "");

  // Strip dangling filler words at the end (conjunctions, prepositions,
  // articles) so the sentence doesn't end mid-clause on "that.", "in.", etc.
  const filler = /\s+(a|an|the|to|of|in|at|on|with|and|or|that|but|for|from|by|as|is|are|was|were|be|been|it|this)$/i;
  while (filler.test(stem)) stem = stem.replace(filler, "");

  if (ellipsis) return `${stem}…`;
  return /[.!?]$/.test(stem) ? stem : `${stem}.`;
}

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * SEO head manager. Sets title, meta, canonical, OG/Twitter and JSON-LD.
 * All titles are auto-truncated to 60 characters and descriptions to 160
 * characters — measured on the FINAL rendered string (with the site suffix).
 * jsonLd: a single object or an array of schema.org objects.
 */
export function Seo({ title, description, path = "", image, jsonLd, noindex = false }) {
  useEffect(() => {
    // Pages own their titles end-to-end (no site suffix appended). Fallback
    // used only for pages that forget to pass a title.
    const rawTitle = title || "Wisconsin Prom & Formal Dresses";
    const fullTitle = _clip(rawTitle, TITLE_MAX, { ellipsis: false });
    const desc = _clip(description, DESC_MAX, { ellipsis: false });

    const url = `${ORIGIN}${path}`;
    const img = image || "https://images.unsplash.com/photo-1595777457583-95e059d581b8?fm=jpg&q=85&w=1200";
    document.title = fullTitle;

    if (desc) upsertMeta("name", "description", desc);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", img);

    // JSON-LD structured data
    document.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove());
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo-jsonld", "true");
        s.textContent = JSON.stringify(block);
        document.head.appendChild(s);
      });
    }
    return () => {
      document.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove());
    };
  }, [title, description, path, image, JSON.stringify(jsonLd), noindex]);

  return null;
}

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${ORIGIN}${it.path}`,
  })),
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export { ORIGIN };
